import React, { useState } from 'react';
import { X, Search, FileText, AlertCircle, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';

const PaymentProcessModal = ({ isOpen, onClose, onNext, customerData }) => {
  const [invoiceNumber, setInvoiceNumber] = useState(customerData?.invoiceNo || '');
  const [fetchedInvoice, setFetchedInvoice] = useState(null);
  const [dueInvoices, setDueInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInvoiceDetails = async () => {
    if (!invoiceNumber.trim()) {
      toast.error('Please enter an invoice number');
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/customer/invoice/${invoiceNumber}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        toast.error('Invoice not found');
        return;
      }

      const data = await response.json();
      setFetchedInvoice(data.invoice);
      
      // Fetch all due invoices for this customer
      const dueResponse = await fetch(`${backendUrl}/api/customer/invoices/due`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (dueResponse.ok) {
        const dueData = await dueResponse.json();
        setDueInvoices(dueData.invoices || []);
      }

      toast.success('Invoice details fetched successfully');
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to fetch invoice details');
    } finally {
      setLoading(false);
    }
  };

  const toggleInvoiceSelection = (invoice) => {
    setSelectedInvoices(prev => {
      const exists = prev.find(inv => inv._id === invoice._id);
      if (exists) {
        return prev.filter(inv => inv._id !== invoice._id);
      } else {
        return [...prev, invoice];
      }
    });
  };

  const calculateTotalDue = () => {
    return selectedInvoices.reduce((total, inv) => total + (inv.amount || 0), 0);
  };

  const handleNext = () => {
    if (selectedInvoices.length === 0 && !fetchedInvoice) {
      toast.error('Please select at least one invoice');
      return;
    }

    const invoicesToPay = selectedInvoices.length > 0 ? selectedInvoices : [fetchedInvoice];
    onNext({
      ...customerData,
      invoices: invoicesToPay,
      totalAmount: calculateTotalDue() || fetchedInvoice?.amount || 0
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 flex justify-between items-center rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold">Payment Process</h2>
            <p className="text-green-100 text-sm mt-1">{customerData?.name} - {customerData?.email}</p>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 rounded-full p-2 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Invoice Number Input */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline w-4 h-4 mr-1" />
              Enter Your Invoice Number
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="INV-001"
              />
              <button
                onClick={fetchInvoiceDetails}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold disabled:opacity-50 flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                {loading ? 'Fetching...' : 'Fetch Detail'}
              </button>
            </div>
          </div>

          {/* Fetched Invoice Details */}
          {fetchedInvoice && (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Invoice Details
              </h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-semibold">Invoice Number:</span> {fetchedInvoice.invoiceNumber}</p>
                <p><span className="font-semibold">Date:</span> {new Date(fetchedInvoice.date).toLocaleDateString()}</p>
                <p><span className="font-semibold">Amount:</span> <span className="text-green-700 font-bold text-lg">₹{fetchedInvoice.amount?.toFixed(2)}</span></p>
                <p><span className="font-semibold">Status:</span> <span className={fetchedInvoice.status === 'Paid' ? 'text-green-600' : 'text-red-600'}>{fetchedInvoice.status}</span></p>
              </div>
            </div>
          )}

          {/* Due Invoices */}
          {dueInvoices.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                All Due Invoices - Select to Pay
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {dueInvoices.map((invoice) => (
                  <div
                    key={invoice._id}
                    onClick={() => toggleInvoiceSelection(invoice)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedInvoices.find(inv => inv._id === invoice._id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckSquare className={`w-5 h-5 ${
                          selectedInvoices.find(inv => inv._id === invoice._id)
                            ? 'text-blue-600'
                            : 'text-gray-400'
                        }`} />
                        <div>
                          <p className="font-semibold">{invoice.invoiceNumber}</p>
                          <p className="text-xs text-gray-500">{new Date(invoice.date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">₹{invoice.amount?.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">Due</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total Due */}
          {selectedInvoices.length > 0 && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="font-bold text-gray-900">Total Due Payment:</span>
                <span className="text-2xl font-bold text-orange-600">₹{calculateTotalDue().toFixed(2)}</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">{selectedInvoices.length} invoice(s) selected</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleNext}
              disabled={selectedInvoices.length === 0 && !fetchedInvoice}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-700 hover:to-teal-700 transition font-semibold disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessModal;
